import { MenuColors } from '../utils/menuColors';
import axios from 'axios';

const getApiBaseUrl = () => {
  const isDevelopment = process.env.NODE_ENV === 'development' ||
                       window.location.hostname === 'localhost' ||
                       window.location.hostname === '127.0.0.1' ||
                       window.location.port === '3002';

  const baseUrl = isDevelopment
    ? (process.env.REACT_APP_API_URL_LOCAL || 'http://localhost:8080')
    : (process.env.REACT_APP_API_URL_PROD || 'https://backend.mvpdelivery.com');

  return `${baseUrl}/api`;
};

const API_BASE_URL = getApiBaseUrl();

const mesaApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export interface Mesa {
  id: string;
  name: string;
  user_id: string;
  permanent_uuid: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Store {
  id: string;
  name: string;
  endereco?: string;
  meios_pagamento?: string;
  cobrar_preco_maior?: boolean;
  hora_fechamento?: string;
  pedido_minimo?: number;
  image_path?: string;
  banner_image_path?: string;
  cores_cardapio?: MenuColors | null;
}

export interface MesaLinkValidation {
  valid: boolean;
  mode: 'mesa';
  mesa?: Mesa;
  store?: Store;
  error?: string;
}

export interface MesaRedirectResponse {
  success: boolean;
  link_id?: string;
  redirect_path?: string;
  error?: string;
}

export interface ProductTypeInput {
  id: string;
  product_type_id: string;
  name: string;
  input_type: 'string' | 'checkbox' | 'dropdown' | 'fixed_checkbox';
  type_description?: string;
  values?: string[];
  preco_diferente?: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProductType {
  id: string;
  type: string;
  attributes: string[];
  product_type_inputs: ProductTypeInput[];
  created_at: string;
  updated_at: string;
}

export interface Product {
  id: string;
  name: string;
  price: number;
  user_id: string;
  product_type_id?: string | null;
  product_type?: ProductType;
  attributes: Record<string, any>;
  eh_promocao?: boolean;
  description?: string | null;
  is_combo?: boolean;
  combo_items?: Array<{ product_id: string; name: string; quantity: number }>;
  menu_position?: number | null;
  image_path?: string;
  created_at: string;
  updated_at: string;
}

export interface Menu {
  [category: string]: Product[];
}

export interface MenuResponse {
  menu: Menu;
  category_order?: string[];
  total_products: number;
}

export interface MesaOrderItem {
  product_id: string;
  quantity: number;
  product_variables?: { [key: string]: any };
}

export interface CreateMesaOrderData {
  items: MesaOrderItem[];
  observacoes?: string;
}

export interface MesaOrderResponse {
  success: boolean;
  order: any;
  daily_order_id: number;
  message: string;
}

/**
 * Obtem redirect para um link temporario a partir do UUID permanente da mesa
 */
export const getMesaRedirect = async (permanentUuid: string): Promise<MesaRedirectResponse> => {
  try {
    const response = await mesaApi.get(`/mesa/${permanentUuid}/redirect`);
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      return error.response.data;
    }
    return {
      success: false,
      error: 'Erro ao acessar mesa'
    };
  }
};

/**
 * Valida um link de mesa temporario
 */
export const validateMesaLink = async (linkId: string): Promise<MesaLinkValidation> => {
  try {
    const response = await mesaApi.get(`/mesa-links/${linkId}/validate`);
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      return error.response.data;
    }
    return {
      valid: false,
      mode: 'mesa',
      error: 'Erro ao validar link'
    };
  }
};

/**
 * Obtem o cardapio para um link de mesa
 */
export const getMesaMenu = async (linkId: string): Promise<MenuResponse> => {
  const response = await mesaApi.get(`/mesa-links/${linkId}/menu`);
  return response.data;
};

/**
 * Cria um pedido de mesa (simplificado - sem nome, endereco, pagamento)
 */
export const createMesaOrder = async (
  linkId: string,
  orderData: CreateMesaOrderData
): Promise<MesaOrderResponse> => {
  const response = await mesaApi.post(`/mesa-links/${linkId}/order`, orderData);
  return response.data;
};

export const mesaOrderAPI = {
  getMesaRedirect,
  validateMesaLink,
  getMesaMenu,
  createMesaOrder,
};

export default mesaOrderAPI;
